import { Link } from "react-router-dom";
import { 
  Shield, 
  CheckCircle, 
  Phone,
  FileText,
  Camera,
  Users,
  Clock,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  Zap,
  Building,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Insurance carriers with links
const CARRIERS = [
  { name: "State Farm", slug: "state-farm", link: "https://www.statefarm.com/claims" },
  { name: "American Family", slug: "american-family", link: "https://www.amfam.com/claims" },
  { name: "Allstate", slug: "allstate", link: "https://www.allstate.com/claims" },
  { name: "Farmers Insurance", slug: "farmers", link: "https://www.farmers.com/claims/" },
  { name: "Progressive", slug: "progressive", link: "https://www.progressive.com/claims/" },
  { name: "Liberty Mutual", slug: "liberty-mutual", link: "https://www.libertymutual.com/claims" },
  { name: "Travelers", slug: "travelers", link: "https://www.travelers.com/claims" },
  { name: "USAA", slug: "usaa", link: "https://www.usaa.com/inet/wc/claims-center" },
  { name: "Nationwide", slug: "nationwide", link: "https://www.nationwide.com/claims/" },
  { name: "Auto-Owners Insurance", slug: "auto-owners", link: "https://www.auto-owners.com/claims" },
  { name: "Western National", slug: "western-national", link: "https://www.wnins.com/claims" },
  { name: "Farm Bureau", slug: "farm-bureau", link: "https://www.fbfs.com/claims" },
  { name: "Safeco", slug: "safeco", link: "https://www.safeco.com/claims" },
  { name: "The Hartford", slug: "hartford", link: "https://www.thehartford.com/claims" },
  { name: "Chubb", slug: "chubb", link: "https://www.chubb.com/us-en/claims.html" },
];

export default function InsuranceClaims() {
  useSEO();

  const benefits = [
    { icon: Camera, text: "Free Roof Inspections" },
    { icon: FileText, text: "Storm Damage Assessments" },
    { icon: Shield, text: "Insurance Claim Guidance" },
    { icon: Camera, text: "Detailed Documentation & Photos" },
    { icon: Users, text: "Adjuster Meeting Assistance" },
    { icon: Building, text: "Local Minnesota Roofing Experts" },
  ];

  const claimSteps = [
    {
      number: "1",
      title: "Document the Damage",
      description: "Take photos and videos of all visible damage. Include wide shots and close-ups. Note the date and time of the storm.",
      icon: Camera
    },
    {
      number: "2",
      title: "Contact Your Insurance",
      description: "Call your insurance company's claims line (not your agent). Get a claim number and ask about your specific coverage.",
      icon: Phone
    },
    {
      number: "3",
      title: "Schedule an Inspection",
      description: "Get a professional roof inspection. StormCare provides free inspections to help document damage for your claim.",
      icon: FileText
    },
    {
      number: "4",
      title: "Meet with the Adjuster",
      description: "Be present when the insurance adjuster visits. Walk them through all damage and share your documentation.",
      icon: Users
    },
    {
      number: "5",
      title: "Review Your Estimate",
      description: "Carefully review the insurance estimate. Ensure all damage is included and the scope matches your contractor's assessment.",
      icon: CheckCircle
    }
  ];

  const faqs = [
    {
      question: "How long do I have to file a claim in Minnesota?",
      answer: "Most Minnesota homeowner policies require you to file a claim within 60 days of discovering the damage. However, it's best to report damage as soon as possible to avoid complications."
    },
    {
      question: "Will filing a claim raise my insurance rates?",
      answer: "It depends on your carrier and claims history. Some insurers don't raise rates for weather-related claims. Ask your agent about your specific policy before filing."
    },
    {
      question: "What's the difference between ACV and RCV coverage?",
      answer: "Actual Cash Value (ACV) pays the depreciated value of your roof. Replacement Cost Value (RCV) pays to replace your roof with similar materials without depreciation. RCV coverage is typically better for homeowners."
    },
    {
      question: "Should I get a roof inspection before filing a claim?",
      answer: "Yes! A professional inspection can help you understand the extent of damage and whether it's worth filing a claim, especially considering your deductible."
    },
    {
      question: "Can I choose my own contractor?",
      answer: "Yes. In Minnesota, you have the right to choose any licensed contractor you prefer, regardless of what your insurance company suggests."
    }
  ];

  return (
    <div data-testid="insurance-claims-page" className="">
      {/* Hero Section */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Shield className="w-4 h-4" />
              Insurance Claims Hub
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-tight mb-6">
              Insurance Claims Made Simple
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed mb-4">
              We Work With All Major Insurance Carriers Across Minnesota
            </p>
            
            <p className="text-gray-400 leading-relaxed mb-8">
              Storm damage is stressful enough. Navigating the insurance process shouldn't be. 
              At StormCare, we work with homeowners and insurance providers throughout Minnesota 
              to help make the roof restoration process as smooth as possible.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="#free-inspection">
                <Button className="bg-orange hover:bg-orange-hover text-white px-6 py-4 text-lg">
                  <Zap className="w-5 h-5 mr-2" />
                  Schedule Free Inspection
                </Button>
              </a>
              <a href="#carriers">
                <Button variant="outline" className="border-gray-600 text-white hover:bg-white/10 px-6 py-4 text-lg">
                  Find Your Insurance Carrier
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit) => (
              <div 
                key={benefit.text}
                className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl p-4"
              >
                <div className="w-10 h-10 bg-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-orange" />
                </div>
                <span className="font-medium text-navy">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to File a Claim Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              How to File a Storm Damage Insurance Claim
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Follow these steps to navigate your insurance claim successfully
            </p>
          </div>

          <div className="space-y-6">
            {claimSteps.map((step, idx) => (
              <div 
                key={step.number}
                className="bg-white border border-gray-200 rounded-xl p-6 flex items-start gap-6"
              >
                <div className="w-14 h-14 bg-orange rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-heading font-bold text-white">{step.number}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-semibold text-navy mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                <step.icon className="w-8 h-8 text-gray-300 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Carriers Grid */}
      <section id="carriers" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Insurance Carriers We Work With
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We've worked with homeowners insured by virtually every major insurance carrier. 
              Click below to start a claim directly with your provider.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {CARRIERS.map((carrier) => (
              <a
                key={carrier.slug}
                href={carrier.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`carrier-${carrier.slug}`}
                className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center hover:border-orange hover:shadow-lg transition-all group"
              >
                <div className="font-medium text-navy group-hover:text-orange transition-colors mb-2">
                  {carrier.name}
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-orange">
                  Start a Claim
                  <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>

          <div className="bg-gray-100 rounded-xl p-6 text-center">
            <p className="text-gray-600">
              <strong>Don't see your insurance company listed?</strong> Contact StormCare. 
              We regularly work with homeowners insured through regional and national carriers 
              throughout Minnesota.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Common Insurance Claim Questions
            </h2>
            <p className="text-gray-600 text-lg">
              Get answers to frequently asked questions about storm damage claims in Minnesota
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <HelpCircle className="w-6 h-6 text-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-semibold text-navy mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/faqs">
              <Button variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white">
                View All FAQs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="free-inspection" className="py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-4">
            Not Sure If You Should File a Claim?
          </h2>
          
          <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Many homeowners aren't sure whether their roof damage is severe enough to involve insurance. 
            Before filing a claim, schedule a free StormCare roof inspection. We'll assess your roof, 
            document any visible storm damage, explain your options, and help you make an informed decision.
          </p>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-300 mb-10">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              Free Inspection
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              Storm Damage Assessment
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              Honest Recommendations
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange" />
              No Obligation
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#get-started">
              <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-6 text-lg w-full sm:w-auto">
                <Zap className="w-5 h-5 mr-2" />
                Schedule My Free Roof Inspection
              </Button>
            </Link>
            <a href="tel:+1-555-STORM-MN">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto">
                <Phone className="w-5 h-5 mr-2" />
                Call StormCare Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Carrier-Specific Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Carrier-Specific Claim Resources
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Learn more about filing roof damage claims with your specific insurance carrier
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {CARRIERS.map((carrier) => (
              <Link
                key={carrier.slug}
                to={`/${carrier.slug}-roof-claims`}
                data-testid={`carrier-resource-${carrier.slug}`}
                className="bg-gray-50 border border-gray-200 rounded-xl p-5 hover:border-orange hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-navy group-hover:text-orange transition-colors mb-2">
                  {carrier.name}
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  How to file a roofing claim
                </p>
                <span className="inline-flex items-center text-sm text-orange">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-xs text-gray-500 text-center">
            <strong>Disclaimer:</strong> StormCare is not a public insurance adjuster and does not determine 
            insurance coverage, claim approval, or claim value. Coverage decisions are made solely by your 
            insurance carrier according to the terms and conditions of your policy.
          </p>
        </div>
      </section>
    </div>
  );
}
